/**
 * Persona Service
 * Handles Persona-related API calls
 */

import api from '@/lib/api';
import { withBuScope } from '@/lib/business-unit-scope';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { PersonaAdapter } from '@/adapters';
import type {
	ApiResponse,
	Persona,
	PersonaChanges,
	PersonaChangesResponseDto,
	PersonaGeneration,
	PersonaResponseDto,
	PersonasListResponseDto,
	PersonaContentResponseDto,
	PersonaVersionsResponseDto,
	PersonaCreateResponseDto
} from '@/types';

export const personaService = {
	/**
	 * Generate a persona for the brand currently in scope.
	 *
	 * The brand rides on the URL, not the body: it decides WHICH TICKETS are sampled and
	 * which row the upsert writes, so it is scope rather than payload and is spelled the
	 * same way here as on the reads.
	 */
	async createPersona(
		data: { scope: 'brand' | 'agent'; agentUserIds?: number[] },
		buScope: { bu_id?: string } = {},
	): Promise<PersonaCreateResponseDto> {
		const payload = PersonaAdapter.toCreateRequest(data);
		const response = await api.post<ApiResponse<PersonaCreateResponseDto>>(
			withBuScope(API_ENDPOINTS.PERSONA.BASE, buScope),
			payload,
		);
		return response.data.data;
	},

	async getPersonas(params?: { bu_id?: string }): Promise<Persona[]> {
		const response = await api.get<ApiResponse<PersonasListResponseDto>>(API_ENDPOINTS.PERSONA.BASE, { params });
		return PersonaAdapter.fromListApi(response.data.data);
	},

	async getPersonaById(id: string): Promise<Persona> {
		const response = await api.get<ApiResponse<PersonaResponseDto>>(API_ENDPOINTS.PERSONA.BY_ID(id));
		return PersonaAdapter.fromDetailApi(response.data.data);
	},

	async getMarkdownContent(id: string, version?: string): Promise<string> {
		const response = await api.get<ApiResponse<PersonaContentResponseDto>>(API_ENDPOINTS.PERSONA.CONTENT(id, version));
		return response.data.data.markdown;
	},

	async getVersionHistory(id: string): Promise<PersonaGeneration[]> {
		const response = await api.get<ApiResponse<PersonaVersionsResponseDto>>(API_ENDPOINTS.PERSONA.VERSIONS(id));
		return PersonaAdapter.fromVersionsApi(response.data.data);
	},

	/** What the last regenerate changed in the guide, with its review; null when the current version is not a regenerate. */
	async getChanges(id: string): Promise<PersonaChanges | null> {
		const response = await api.get<ApiResponse<PersonaChangesResponseDto>>(API_ENDPOINTS.PERSONA.CHANGES(id));
		return PersonaAdapter.fromChangesApi(response.data.data);
	},

	async refinePersona(id: string, feedbackText: string): Promise<{ jobId: string }> {
		const payload = PersonaAdapter.toRefineRequest(feedbackText);
		const response = await api.post<ApiResponse<{ jobId: string }>>(API_ENDPOINTS.PERSONA.REFINE(id), payload);
		return response.data.data;
	},

	async validateExamples(
		id: string,
		examples: Array<{ exampleId: string; response: 'looks_good' | 'respond_differently'; alternativeText?: string }>
	): Promise<{ persona?: Persona; jobId?: string }> {
		const payload = PersonaAdapter.toValidateExamplesRequest(examples);
		const response = await api.post<ApiResponse<PersonaResponseDto | { jobId: string }>>(
			API_ENDPOINTS.PERSONA.VALIDATE_EXAMPLES(id),
			payload
		);
		const data = response.data.data;
		if ('persona' in data) return { persona: PersonaAdapter.fromDto(data.persona) };
		return { jobId: data.jobId };
	},

	/** Returns the persona to its last validated version, undoing the versions since. */
	async restoreValidatedVersion(id: string): Promise<Persona> {
		const response = await api.post<ApiResponse<PersonaResponseDto>>(API_ENDPOINTS.PERSONA.RESTORE_VALIDATED(id));
		return PersonaAdapter.fromDetailApi(response.data.data);
	},

	async deletePersona(id: string): Promise<void> {
		await api.delete(API_ENDPOINTS.PERSONA.BY_ID(id));
	}
};
